// Create + edit an admin/manager account. Password is required on create,
// optional on edit (blank = keep current password).

import { useEffect } from 'react';
import { Form, Input, Modal, Select, Switch, message } from 'antd';
import type { AdminUser, CreateAdminUserInput } from './admin-users.api';
import { useCreateAdminUser, useUpdateAdminUser } from './useAdminUsers';

interface Props {
  open: boolean;
  editing: AdminUser | null;
  onClose: () => void;
}

interface FormValues {
  username: string;
  password?: string;
  role: 'admin' | 'manager';
  isActive: boolean;
}

export function UserFormModal({ open, editing, onClose }: Props) {
  const [form] = Form.useForm<FormValues>();
  const create = useCreateAdminUser();
  const update = useUpdateAdminUser();
  const isEdit = !!editing;

  useEffect(() => {
    if (open) {
      if (editing) {
        form.setFieldsValue({
          username: editing.username,
          role: editing.role,
          isActive: editing.isActive,
          password: '',
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, editing, form]);

  async function handleOk() {
    try {
      const values = await form.validateFields();
      if (editing) {
        const input: Record<string, unknown> = {
          username: values.username,
          role: values.role,
          isActive: values.isActive,
        };
        if (values.password) input.password = values.password; // blank = keep
        await update.mutateAsync({ id: editing.id, input });
      } else {
        await create.mutateAsync(values as CreateAdminUserInput);
      }
      message.success('Đã lưu');
      onClose();
    } catch (err) {
      const apiMessage = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      if (apiMessage) message.error(apiMessage);
    }
  }

  return (
    <Modal
      open={open}
      title={isEdit ? 'Sửa tài khoản' : 'Tạo tài khoản mới'}
      onCancel={onClose}
      onOk={() => void handleOk()}
      confirmLoading={create.isPending || update.isPending}
      destroyOnClose
    >
      <Form form={form} layout="vertical" preserve={false} initialValues={{ isActive: true, role: 'manager' }}>
        <Form.Item
          name="username"
          label="Tên đăng nhập"
          rules={[{ required: true, min: 3, max: 64 }]}
        >
          <Input autoComplete="off" autoFocus disabled={isEdit} />
        </Form.Item>
        <Form.Item
          name="password"
          label={isEdit ? 'Mật khẩu mới (để trống = giữ nguyên)' : 'Mật khẩu'}
          rules={isEdit ? [{ min: 8, message: 'Tối thiểu 8 ký tự' }] : [{ required: true, min: 8 }]}
        >
          <Input.Password autoComplete="new-password" placeholder={isEdit ? '••••••••' : ''} />
        </Form.Item>
        <Form.Item name="role" label="Vai trò" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'admin', label: 'Admin (toàn quyền, quản lý user)' },
              { value: 'manager', label: 'Manager (CRUD customer + license)' },
            ]}
          />
        </Form.Item>
        <Form.Item name="isActive" label="Hoạt động" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
